import * as React from 'react';

import { Moment } from 'moment';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { sendBulkMessage } from '../../contacts/actions/send-bulk-message';
import { sendText } from '../../contacts/actions/send-text';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { employeeContextSelector } from '../../reducers/contact-selectors';
import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';

import { stateCityOptionalStepProvider } from '../../employees/city-state-provider';
import { EmployeeTextMessageList } from '../../employees/employee-text-message-list';
import * as WebRoutes from '../../web-routes';
import { EmployeeMessageTemplates } from '../message-composer/employee-message-templates';

import { employeeName } from '../../employees/utils/employee-name';
import { EmployeeCanMessage } from '../../util/can-message';
import { LANGUAGES } from '../../util/languages';
import { MESSAGE_TYPE } from '../../util/message-types';
import { TEMPLATE_TYPE } from '../../util/template-type';
import { ContactMessageErrors } from '../../widgets/contact-message-errors';
import MessageWidget from '../../widgets/legacy-message';
import { MaterialReactSelect } from '../../widgets/material-react-select';
import { SingleLanguageComposerWithPlugins } from '../../widgets/message-composer/wrappers/single-language-composer-with-plugins';

const mapStateToProps = (state: any, ownProps: InputProps) => ({
  employees: employeesForOrgIdSelector(state, ownProps.orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    sendMessage: (
      contact: { contactId: number; context: ContactContextType },
      messageBody: string,
      messageType: string,
      subject?: string | null,
      sendDateTime?: Moment | null,
      urlMappings?: Record<string, string>,
      sendVCardForOrgId?: number,
    ) =>
      dispatch(
        sendText(
          contact.contactId,
          contact.context,
          messageBody,
          messageType,
          subject,
          { orgId },
          sendDateTime,
          urlMappings,
          sendVCardForOrgId,
        ),
      ),
    sendBulkMessage: (
      contacts: Array<{ contactId: number; context: ContactContextType }>,
      messageBody: string,
      messageType: string,
      hasVCardAttached: boolean,
      subject?: string | null,
      sendDateTime?: Moment | null,
      urlMappings?: Record<string, string>,
    ) =>
      dispatch(
        sendBulkMessage(
          contacts,
          messageBody,
          messageType,
          hasVCardAttached,
          subject,
          { orgId },
          sendDateTime,
          urlMappings,
        ),
      ),
    loadOrgEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
  },
});

type InputProps = {
  orgId: number;
  onMessageSent: (employeeId: number) => void;
  onClose: () => void;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

type State = {
  selectedEmployees: Array<SelectOption<string>>;
  messageType: string | null;
  message: string | null;
};

class EmployeeAddNewMessageComponent extends React.PureComponent<Props, State> {
  state: State = {
    selectedEmployees: [],
    messageType: null,
    message: null,
  };

  componentDidMount() {
    this.props.actions.loadOrgEmployees();
  }

  componentDidUpdate() {
    this.props.actions.loadOrgEmployees();
  }

  getEmployeeOptions = () => {
    const { employees } = this.props;
    const arr = employees || [];
    return arr
      .filter((e: Employee) => {
        const canMessage = new EmployeeCanMessage(e);
        return (
          e.isCurrentEmployee &&
          (canMessage.canReceive(MESSAGE_TYPE.TEXT) || canMessage.canReceive(MESSAGE_TYPE.EMAIL))
        );
      })
      .map((e: Employee) => ({
        label: employeeName(e),
        value: e.employeeId.toString(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  getSelectedEmployees = (): Array<Employee> => {
    const selectedEmployeeIds = this.state.selectedEmployees.map((se) => se.value);
    return this.props.employees.filter((e) =>
      selectedEmployeeIds.includes(e.employeeId.toString()),
    );
  };

  handleEmployeeSelection = (selectedEmployees: Array<{ value: string; label: string }>) => {
    this.setState({ selectedEmployees: selectedEmployees || [] });
  };

  handleMessageTypeChange = (messageType: string) => {
    this.setState({ messageType });
  };

  getWarning = () => {
    const employees = this.getSelectedEmployees();
    const { messageType } = this.state;
    const missingContactInfo = employees.filter((e) => {
      const canMessage = new EmployeeCanMessage(e);
      if (messageType === MESSAGE_TYPE.TEXT) {
        return !canMessage.canReceive(MESSAGE_TYPE.TEXT);
      }
      return !canMessage.canReceive(MESSAGE_TYPE.EMAIL);
    });
    if (missingContactInfo.length > 0) {
      const isPlural = missingContactInfo.length > 1;
      const missingContactInfoString = missingContactInfo.map((e: Employee, index: number) => (
        <Link
          key={`employee-link-${e.employeeId}`}
          to={WebRoutes.employeeRoute(e.orgId, e.employeeId)}
          target="_blank"
        >
          {e.Contact.Person.firstName} {e.Contact.Person.lastName}
          {index + 1 === missingContactInfo.length ? '' : ', '}
        </Link>
      ));
      return (
        <span>
          {isPlural ? 'These' : 'This'} employee{isPlural ? 's' : ''} cannot receive {messageType}{' '}
          messages: {missingContactInfoString}
        </span>
      );
    }
    return null;
  };

  handleSendMessage = async (
    message: string,
    type: string,
    subject?: string | null,
    sendDateTime?: Moment | null,
    optionalFields?: { urlFileMappings?: Record<string, string>; sendWithVCard?: boolean },
  ) => {
    const { actions, onMessageSent } = this.props;
    const { messageType } = this.state;
    const sendVCardForOrgId = optionalFields?.sendWithVCard ? this.props.orgId : undefined;
    const contacts = this.getSelectedEmployees()
      .filter((e) => {
        const canMessage = new EmployeeCanMessage(e);
        if (messageType === MESSAGE_TYPE.TEXT) {
          return canMessage.canReceive(MESSAGE_TYPE.TEXT);
        }
        return canMessage.canReceive(MESSAGE_TYPE.EMAIL);
      })
      .map((e) => ({
        contactId: e.contactId,
        context: employeeContextSelector(e.employeeId),
      }));
    if (contacts.length === 0) {
      return {
        errorMessage: 'No employees would receive this message',
      };
    }

    let response;
    const [firstContact] = contacts;

    if (contacts.length === 1) {
      response = await actions.sendMessage(
        firstContact,
        message,
        type,
        subject,
        sendDateTime,
        optionalFields?.urlFileMappings,
        sendVCardForOrgId,
      );
    } else {
      // We pull the orgId/vCardPath when we hit the sendMessage method. Because these messages get scheduled, unlike the single message above,
      // we will store that they should have a contact card sent and grab the contact card later
      const hasVCardAttached = !!sendVCardForOrgId;
      response = await actions.sendBulkMessage(
        contacts,
        message,
        type,
        hasVCardAttached,
        subject,
        sendDateTime,
        optionalFields?.urlFileMappings,
      );
    }

    if (onMessageSent && firstContact.context.employeeId) {
      onMessageSent(firstContact.context.employeeId);
    }

    return response;
  };

  selectTemplate = (message: string) => {
    this.setState({ message });
  };

  render() {
    const { selectedEmployees, message, messageType } = this.state;
    const { orgId } = this.props;

    let selectedEmployeeId = null;
    let selectedEmployee = null;
    let canMessage = null;
    let enableText = true;
    let enableEmail = true;
    if (selectedEmployees.length === 1) {
      [selectedEmployee] = this.getSelectedEmployees();
      selectedEmployeeId = selectedEmployee.employeeId;
      canMessage = new EmployeeCanMessage(selectedEmployee);
      enableText = canMessage.canReceive(MESSAGE_TYPE.TEXT);
      enableEmail = canMessage.canReceive(MESSAGE_TYPE.EMAIL);
    }

    const warning = this.getWarning();

    return (
      <div className="u-display-flex u-flexDirection-column u-height-100 u-overflow-auto">
        <div className="u-padding u-no-padding-bottom">
          <MaterialReactSelect
            value={selectedEmployees}
            onChange={this.handleEmployeeSelection}
            options={this.getEmployeeOptions()}
            placeholder="Select employees to send a message to"
            autofocus={!selectedEmployeeId && selectedEmployees.length === 0}
          />
        </div>
        <div className="u-height-100 u-overflow-auto u-padding-xl">
          <div className="u-margin-bottom">
            {warning ? (
              <MessageWidget variant="warning" canBeDismissed={false} dataCy="new-message-warning">
                {warning}
              </MessageWidget>
            ) : null}
            {canMessage ? (
              <ContactMessageErrors canMessage={canMessage} contactType="employee" />
            ) : null}
          </div>
          <div className="u-width-100 u-maxWidth-800px u-margin-0Auto">
            <div>
              {selectedEmployeeId ? (
                <EmployeeTextMessageList employeeId={selectedEmployeeId} />
              ) : null}
              {Boolean(selectedEmployee?.Contact?.Person?.languagePreference) &&
              selectedEmployee?.Contact?.Person?.languagePreference !== 'en' ? (
                <MessageWidget variant="warning" canBeDismissed={false}>
                  <div className="typo-subtitle">
                    This employee prefers to communicate in{' '}
                    {LANGUAGES[selectedEmployee?.Contact.Person.languagePreference ?? 'en']}
                  </div>
                </MessageWidget>
              ) : null}
              <SingleLanguageComposerWithPlugins
                orgId={orgId}
                message={message}
                enableText={enableText}
                enableEmail={enableEmail}
                initialType={enableEmail && !enableText ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT}
                onMessageTypeChange={this.handleMessageTypeChange}
                onSendMessage={this.handleSendMessage}
                autofocus={Boolean(selectedEmployeeId)}
                onChange={this.selectTemplate}
                canScheduleMessageInFuture
                disabled={!selectedEmployeeId && selectedEmployees.length === 0}
                allowSendWithVCard
              />
              {(enableText || enableEmail) && selectedEmployees.length > 0 ? (
                <EmployeeMessageTemplates
                  orgId={this.props.orgId}
                  employeeId={selectedEmployeeId}
                  onSelect={this.selectTemplate}
                  templateType={TEMPLATE_TYPE.MESSAGE}
                  messageType={messageType}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const EmployeeAddNewMessage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(stateCityOptionalStepProvider((o) => o.phoneNumber, EmployeeAddNewMessageComponent));
